const navigationEvent = 'getfishnet:navigate';

// These workspaces keep their existing view/RBAC identifiers, but belong to Settings.
export const settingsWorkspaceRoutes = ['automations', 'properties', 'newsletter', 'analytics'] as const;
export const settingsSections = [
  'organization', 'locales', 'contacts', 'opportunities', 'proposals', 'appointments',
  'finance', 'emails', 'calendars', 'integrations', 'migration',
] as const;

export function resolveSettingsSection(section?: string, personal = false) {
  return settingsSections.includes(section as (typeof settingsSections)[number])
    ? section!
    : personal ? 'emails' : 'organization';
}

const normalizedPath = (path: string) => {
  const clean = path.trim().replace(/^#?\/*/, '').replace(/\/+$/, '');
  return clean ? `/${clean}` : '/';
};

export function canonicalPathname(path: string) {
  const normalized = normalizedPath(path);
  const [route] = normalized.slice(1).split('/');
  return route === 'integrations' || settingsWorkspaceRoutes.includes(route as (typeof settingsWorkspaceRoutes)[number])
    ? `/settings${normalized}`
    : normalized;
}

const routeParts = (path: string) => {
  const match = path.trim().replace(/^#(?=\/)/, '').match(/^([^?#]*)(\?[^#]*)?(#.*)?$/)!;
  return { pathname: canonicalPathname(match[1] || '/'), search: match[2], hash: match[3] || '' };
};

/** Replace aliases in place, including after Back/Forward; never add a redirect entry. */
export function migrateLegacySettingsUrl() {
  const path = canonicalPathname(window.location.pathname);
  if (path === window.location.pathname) return false;
  window.history.replaceState(window.history.state, '', `${path}${window.location.search}${window.location.hash}`);
  return true;
}

export function migrateLegacyHashUrl() {
  const match = window.location.hash.match(/^#\/(.*)$/);
  if (!match) return migrateLegacySettingsUrl();
  const destination = routeParts(match[1] || '');
  window.history.replaceState(window.history.state, '', `${destination.pathname}${destination.search ?? window.location.search}${destination.hash}`);
  return true;
}

export function currentPath() {
  return canonicalPathname(window.location.pathname);
}

export function currentPathSegments() {
  return currentPath().replace(/^\//, '').split('/').filter(Boolean);
}

export function navigatePath(path: string, options: { replace?: boolean; search?: string } = {}) {
  const parts = routeParts(path);
  const search = options.search ?? parts.search ?? window.location.search;
  const destination = `${parts.pathname}${search}${parts.hash}`;
  if (options.replace) window.history.replaceState(null, '', destination);
  else window.history.pushState(null, '', destination);
  window.dispatchEvent(new Event(navigationEvent));
}

export function onPathNavigation(listener: () => void) {
  const handle = () => {
    migrateLegacyHashUrl();
    listener();
  };
  window.addEventListener('popstate', handle);
  window.addEventListener(navigationEvent, handle);
  return () => {
    window.removeEventListener('popstate', handle);
    window.removeEventListener(navigationEvent, handle);
  };
}
